<template>
  <v-container class="pa-0" fluid id="step19">
    <StationWaterQualityChart id="waterqualitystationChart" :requestData="graphRequestData" />
    <v-divider />
    <v-card>
      <v-card-actions wrap class="card-bg-2 py-0 layout wrap">
        <v-btn class="my-2 ml-0 mr-2" :disabled="hasDepthHeightData" color="info" @click="generateGraph" id="step18">
          <v-icon class="mr-1">insert_chart</v-icon>
          <div class="text-capitalize">{{ $t('Button.Generate_Graph') }}</div>
        </v-btn>
        <v-btn class="my-2 ml-0 mr-2" @click="generateTabular()">
          <v-icon class="mr-1">storage</v-icon>
          <div class="text-capitalize">{{ $t('Button.View_As_Tabular') }}</div>
        </v-btn>
        <v-btn class="my-2 ml-0" :loading="loadingDownloadData" @click="downloadData">
          <v-icon class="mr-1">save_alt</v-icon>
          <div class="text-capitalize">{{ $t('Button.DownloadExcelReport') }}</div>
        </v-btn>
      </v-card-actions>

      <div style="background: white;">
        <v-alert transition="slide-y-transition" outline class="ma-0" type="warning" :value="analyteMediaTypes.length === 0">
          <div class="primary--text">This station does not have any sample analyses available</div>
        </v-alert>

        <v-alert transition="slide-y-transition" outline class="ma-0" type="warning" :value="hasDepthHeightData">
          <div class="primary--text">Cannot generate graph for data with depth measurements</div>
        </v-alert>

        <v-alert transition="slide-y-transition" type="info" outline class="ma-0" :value="showErrorBox && (noSelectionWarning || invalidDatesWarning)">
          <div class="primary--text" v-if="noSelectionWarning">Please select at least one analyte.</div>
          <div class="primary--text" v-if="invalidDatesWarning">Please pick a date range.</div>
        </v-alert>
      </div>

      <v-divider />

      <template v-if="viewTabularRequestData">
        <StationWaterQualityTabular id="waterqualitystationChart" @close="onTabularClose()" :requestData="viewTabularRequestData" @refresh="refreshCurrentMFDetails(refreshParams.wqData)" />
        <v-divider />
      </template>

      <v-layout wrap class="card-bg-2">
        <v-flex xs12 md5 class="divider-right">
          <v-card-title class="headline pb-0">Media Types</v-card-title>
          <v-card-text class="py-0">
            <v-radio-group v-if="analyteMediaTypes && analyteMediaTypes.length > 0" v-model="selectedMediaType" row>
              <template v-for="m in analyteMediaTypes">
                <v-radio color="info" :key="m" :label="m" :value="m"></v-radio>
              </template>
            </v-radio-group>
            <div class="py-4 secondary--text text--lighten-1" v-if="!analyteMediaTypes || analyteMediaTypes == 0">
              There are no media types available
            </div>
          </v-card-text>
          <v-divider />

          <v-card-title class="headline">Date Range</v-card-title>
          <v-card-text class="py-0">
            <DateRange v-model="dates" rounded solo :minDate="minDate" :maxDate="maxDate" :selectedRange.sync="selectedRange" :availableDateRange="{ start: minDate, end: maxDate }" :min-max-placeholder="formats.prettyDateFormat" :shortcutPrefix="'Show'" :shortcuts="shortcuts" no-arrows :data="availableData" :shortcutKey="shortcutKey" :loadingValue="loadingValue" />
          </v-card-text>
          <v-divider />

          <WaterQualityGuideline @onGuidelineSelect="onGuidelineSelectHandler" :selectedMedia="selectedMediaType" shouldSetDefaultSelection id="step16" />
          <v-divider />

          <v-card-title class="headline">Options</v-card-title>
          <v-card-text class="py-0">
            <div class="body-2 baseline">
              Include QA/QC sample data
              <v-menu offset-y open-on-hover :open-delay="400">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" class="clickable ml-2" color="primary">
                    info_outline
                  </v-icon>
                </template>
                <v-card>
                  <v-card-text>
                    Include data from QA/QC replicate samples.
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            <v-radio-group v-model="includeQaQc" class="mt-1 mb-0" row>
              <v-radio color="info" label="Yes" :value="true" />
              <v-radio color="info" label="No" :value="false" />
            </v-radio-group>
          </v-card-text>

          <v-divider />

          <v-card-title class="headline">Download Options</v-card-title>
          <v-card-text class="py-0">
            <div class="body-2 baseline">
              Report Format
              <v-menu offset-y open-on-hover :open-delay="400" :close-delay="400">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" class="clickable ml-2" color="primary">
                    info_outline
                  </v-icon>
                </template>
                <v-card>
                  <v-card-text>
                    Only the standard excel format is available at the moment. For other formats, try the <br />
                    <router-link target="_blank" :to="{ name: 'discreteDataQueryReport'}"> Discrete Data Query Report
                    </router-link>.
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            <v-radio-group :value="true" class="mt-1 mb-4" row>
              <v-radio color="info" :value="true">
                <template v-slot:label>
                  <div>
                    <div class="font-weight-">Standard</div>
                    <div class="caption">Excel</div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
            <div class="body-2 baseline">
              Include Envirodata analyte name in report
              <v-menu offset-y open-on-hover :open-delay="400" :close-delay="400">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" class="clickable ml-2" color="primary">
                    info_outline
                  </v-icon>
                </template>
                <v-card>
                  <v-card-text>
                    Add a column in the excel report to show the envirodata analyte name.<br />
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            <v-radio-group v-model="includeAnalyteName" class="mt-1 mb-4" row>
              <v-radio color="info" :value="true">
                <template v-slot:label>
                  <div>
                    <div class="font-weight-">Yes</div>
                  </div>
                </template>
              </v-radio>
              <v-radio color="info" :value="false">
                <template v-slot:label>
                  <div>
                    <div class="font-weight-">No</div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-card-text>
          <v-divider />
        </v-flex>

        <v-flex xs12 md7 class="divider-left" id="step17">
          <v-card-title>
            <div class="headline pb-0" style="align-items: baseline; display: flex;">
              Analytes
            </div>
            <v-spacer />
            <v-text-field prepend-inner-icon="search" v-model="search" class="rounded font-weight-regular" hide-details clearable solo placeholder="Search analytes" @keyup.enter="toggleCurrentItems($event)" @click:clear="clearSearch()" />
          </v-card-title>

          <v-switch v-model="showAnalyteName" label="Show Envirodata Analyte Name" color="primary" hide-details class="switch"></v-switch>
          <v-menu offset-y open-on-hover :open-delay="400" :close-delay="400">
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="clickable ml-2" color="primary">
                info_outline
              </v-icon>
            </template>
            <v-card max-width="500">
              <v-card-text>
                Internal EnviroData names are unique analyte names that help us manage analytes and potential synonyms.
                Display names are not unique and the internal EnviroData name will provide an indication of the
                difference between repeating display names.
              </v-card-text>
            </v-card>
          </v-menu>

          <v-divider />

          <v-item-group mandatory v-model="filterToggle" active-class="white--text">
            <v-subheader style="display: inline; cursor: default;">Show:</v-subheader>
            <v-item>
              <v-chip small slot-scope="{ active, toggle }" :color="active ? 'primary' : 'default'" :selected="active" class="chipFilter" @click="toggle">All</v-chip>
            </v-item>
            <v-item>
              <v-chip small slot-scope="{ active, toggle }" :color="active ? 'primary' : 'default'" :selected="active" class="chipFilter" @click="toggle">
                <v-avatar class="chip-log-count" style="border-radius: 0px;">{{ selected.length }}</v-avatar>
                Selected
              </v-chip>
            </v-item>
          </v-item-group>

          <v-divider />

          <v-data-table ref="analytesTable" v-model="selected" :search="search" :headers="headers" :items="items" class="analyteTable" select-all :pagination.sync="pagination" :item-key="ID_KEY" :custom-sort="customSorter" :custom-filter="customFilter" :loading="loadingValue">
            <template v-slot:headers="props">
              <tr>
                <th>
                  <v-checkbox color="primary" :input-value="props.all" :indeterminate="props.indeterminate" primary hide-details @click.stop="toggleAll()"></v-checkbox>
                </th>
                <th v-for="header in props.headers" :key="header.text" :class="['column sortable', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']" @click="changeSort(header.value)">
                  <v-icon small>arrow_upward</v-icon>
                  {{ header.text }}
                </th>
              </tr>
            </template>
            <template v-slot:items="props">
              <tr class="clickable" :active="props.selected" @click="props.selected = !props.selected">
                <td>
                  <v-checkbox @click.prevent color="info" hide-details :input-value="props.selected" />
                </td>
                <td>{{ props.item.DisplayAnalyte }}</td>
                <td v-if="showAnalyteName">{{ props.item.Analyte }}</td>
                <td class="text-xs-center">
                  {{ formatDateTime(props.item.FirstRecordDate, prettyDateTimeFormat) }}
                </td>
                <td class="text-xs-center">
                  {{ formatDateTime(props.item.LastRecordDate, prettyDateTimeFormat) }}
                </td>
                <td class="text-xs-center">{{ props.item.NumberOfRecords }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-card>
    <water-quality-reports-error-modal v-model="errorModal" :data="errorModalData" :loading="errorModalLoading" :customTitle="customTitle" @download="onErrorModalCallback" @cancel="onErrorModalClose" />
  </v-container>
</template>

<script>
  import moment from 'moment'
  import {
    createNamespacedHelpers,
  } from 'vuex'

  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
  const dataSources = createNamespacedHelpers('datasource')
  const stations = createNamespacedHelpers('stations')
  const authenticate = createNamespacedHelpers('authenticate')
  const {
    mapGetters,
  } = createNamespacedHelpers('guidelines')

  import {
    SOURCE_TYPES,
    SOURCE_TYPES_DATA_TYPES,
  } from '@/store/MonitoringFeature/monitoringfeature'
  import DataTable from '@/components/mixins/DataTable'

  import {
    prettyDateTimeFormat,
    prettyDateFormat,
    newDateStringNoTimezone,
    backendDateTimeFormat,
    newDateStringNoTimezoneAddOneDay,
  } from '@/components/mixins/MomentUtil'

  import {
    defaultTableSorter,
    objectSorter,
    stringSorter,
    datetimeSorter,
    numberSorter,
  } from '@/helpers/sorting'

  import {
    max,
    min,
  } from 'date-fns'

  import DownloadUtil from '@/components/mixins/DownloadUtil'
  import ReportsErrorModalUtil from '@/components/mixins/ReportsErrorModalUtil'
  import DateRange from '@/components/generic/DateRange'
  import WaterQualityReportsErrorModal from '@/components/WaterQualityReportsErrorModal'

  import StationUtil from '@/components/mixins/StationUtil'
  import StationWaterQualityChart from './StationWaterQualityChart'
  import WaterQualityGuideline from './WaterQualityGuideline'
  import StationWaterQualityTabular from './StationWaterQualityTabular'


  import {
    customSearcher,
    getObjectValues,
  } from '@/helpers/search'

  import {
    getTotalNumberOfRecordsWithDepthHeight,
  } from '@/helpers/dataSources/discreteEnvirodata'

  import {
    mergeDistinctObjectArrays,
  } from '@/helpers/arrays'

  const filterToggles = {
    all: 0,
    selected: 1,
  }

  const ID_KEY = 'Id'
  const SEARCH_KEY = 'DisplayAnalyte'

  export default {
    components: {
      DateRange,
      StationWaterQualityChart,
      StationWaterQualityTabular,
      WaterQualityGuideline,
      'water-quality-reports-error-modal': WaterQualityReportsErrorModal,
    },
    mixins: [DataTable, DownloadUtil, StationUtil, ReportsErrorModalUtil],
    data() {
      return {
        ID_KEY,
        prettyDateTimeFormat,
        filter: {
          items: [],
        },
        selectedRange: null,
        selected: [],
        availableData: [],
        itemsMetadata: {},
        previousIndex: '',
        previousItems: [],
        filterToggles,
        filterToggle: 0,
        search: '',
        allCalendarMarks: [],
        pagination: {
          sortBy: SEARCH_KEY,
          rowsPerPage: 10,
          page: 1,
        },
        includeQaQc: false,
        selectedDatasets: null,
        selectedGuidelines: [],
        graphRequestData: null,
        viewTabularRequestData: null,
        // For the highlighting and disabling of future daterangepicker feature
        dates: {
          start: null,
          end: null,
        },
        minDate: null,
        maxDate: null,
        showErrorBox: false,
        selectedMediaType: '',
        flagColorDic: new Map(),
        formats: {
          prettyDateFormat,
        },
        defaultDateFormat: {
          format: prettyDateTimeFormat,
        },
        loadingValue: true,
        loadingDownloadData: false,
        refreshParams: {
          wqData: {
            sourceTypes: [SOURCE_TYPES.discreteEnvirodata],
            sourceTypeProperties: [
              [SOURCE_TYPES_DATA_TYPES.wqData],
            ],
          },
        },
        showAnalyteName: false,
        includeAnalyteName: false,
        shortcutKey: 'pastMonthWithData',
      }
    },
    computed: {
      ...monitoringFeatures.mapGetters([
        'currentFeatureDataSourceIndexByType',
        'currentFeatureDataSourceData',
      ]),
      ...dataSources.mapGetters([
        'dataSourceIndex',
      ]),
      ...stations.mapGetters(['filterWaterQualityDetail', 'CalendarMark']),
      ...authenticate.mapGetters(['currentUserGroup']),
      ...mapGetters({
        'Guidelines': 'guidelineOfCurrentTenant',
      }),
      featureDataSource() {
        return this.currentFeatureDataSourceIndexByType[SOURCE_TYPES.discreteEnvirodata]
      },
      discreteStation() {
        return this.dataSourceIndex[SOURCE_TYPES.discreteEnvirodata][this.featureDataSource.SourceId]
      },
      stationName() {
        return this.discreteStation.LocationIdentifier
      },
      discreteData() {
        return this.currentFeatureDataSourceData(SOURCE_TYPES.discreteEnvirodata)
      },
      waterQualityData() {
        return (this.discreteData && this.discreteData.wqData) || {}
      },
      datasets() {
        return (this.waterQualityData && this.waterQualityData.WaterQualityDatasets) || []
      },
      shortcuts() {
        return {
          all: {
            name: () => 'All available data',
            make: () => {
              return {
                start: moment(this.minDate),
                end: moment(this.maxDate),
              }
            },
          },
          pastMonthWithData: {
            key: 'pastMonthWithData',
            index: 1,
            name: () => 'Past month with available data',
            make: () => {
              return {
                start: moment(this.maxDate).clone().add(-1, 'months'),
                end: moment(this.maxDate),
              }
            },
            isMatch: (start, end, availableDates) => {
              if (!start || !end) return false
              return (end.clone().add(-1, 'months').isSame(start, 'days') && availableDates.end.isSame(end, 'days'))
            },
          },
          pastTwoWeekWithData: {
            key: 'pastTowWeekWithData',
            index: 2,
            name: () => 'Past two weeks with available data',
            make: () => {
              return {
                start: moment(this.maxDate).clone().add(-14, 'days'),
                end: moment(this.maxDate),
              }
            },
            isMatch: (start, end, availableDates) => {
              if (!start || !end) return false
              return (end.clone().add(-14, 'days').isSame(start, 'days') && availableDates.end.isSame(end, 'days'))
            },
          },
          pastThreeMonthsWithData: {
            key: 'pastThreeMonthsWithData',
            index: 3,
            name: () => 'Past three months with available data',
            make: () => {
              return {
                start: moment(this.maxDate).clone().add(-3, 'months'),
                end: moment(this.maxDate),
              }
            },
            isMatch: (start, end, availableDates) => {
              if (!start || !end) return false
              return (end.clone().add(-3, 'months').isSame(start, 'days') && availableDates.end.isSame(end, 'days'))
            },
          },
        }
      },
      allHeaders() {
        return this.showAnalyteName ? {
          displayAnalyte: {
            text: 'Display Name',
            align: 'center',
            value: 'DisplayAnalyte',
            sorter: objectSorter('DisplayAnalyte', stringSorter),
          },
          analyte: {
            text: 'Envirodata Analyte Name',
            align: 'center',
            value: 'Analyte',
            sorter: objectSorter('Analyte', stringSorter),
          },
          firstRecordDate: {
            text: 'First Record',
            align: 'center',
            value: 'FirstRecordDate',
            sorter: objectSorter('FirstRecordDate', datetimeSorter),
            format: (value) => this.formatDateTime(value, this.defaultDateFormat),
          },
          lastRecordDate: {
            text: 'Last Record',
            align: 'center',
            value: 'LastRecordDate',
            sorter: objectSorter('LastRecordDate', datetimeSorter),
            format: (value) => this.formatDateTime(value, this.defaultDateFormat),
          },
          numberOfRecords: {
            text: '# Records',
            align: 'center',
            value: 'NumberOfRecords',
            sorter: objectSorter('NumberOfRecords', numberSorter),
          },
        } : {
          displayAnalyte: {
            text: 'Display Name',
            align: 'center',
            value: 'DisplayAnalyte',
            sorter: objectSorter('DisplayAnalyte', stringSorter),
          },
          firstRecordDate: {
            text: 'First Record',
            align: 'center',
            value: 'FirstRecordDate',
            sorter: objectSorter('FirstRecordDate', datetimeSorter),
            format: (value) => this.formatDateTime(value, this.defaultDateFormat),
          },
          lastRecordDate: {
            text: 'Last Record',
            align: 'center',
            value: 'LastRecordDate',
            sorter: objectSorter('LastRecordDate', datetimeSorter),
            format: (value) => this.formatDateTime(value, this.defaultDateFormat),
          },
          numberOfRecords: {
            text: '# Records',
            align: 'center',
            value: 'NumberOfRecords',
            sorter: objectSorter('NumberOfRecords', numberSorter),
          },
        }
      },
      headers() {
        return Object.values(this.allHeaders)
      },
      analyteMediaTypes() {
        let analytes = new Set()
        this.datasets.forEach(ds => analytes.add(ds.MediaType))
        return [...analytes]
      },
      items() {
        if (!(this.CalendarMark || this.CalendarMark.DateTimeGroups)) {
          return []
        }
        let data = this.datasets
        if (!data) return []

        // let items = data.items.Datasets.filter(x => x.MediaType == this.selectedMediaType)
        let items = data.filter(x => x.MediaType == this.selectedMediaType)

        this.getDateRange(items)

        this.makeItemsMetadata(items)
        this.loadingValue = false
        return items

      },
      hasDepthHeightData() {
        return getTotalNumberOfRecordsWithDepthHeight(this.datasets) > 0
      },
      noSelectionWarning() {
        return (!this.selected.length)
      },
      invalidDatesWarning() {
        return !(this.dates.start && this.dates.end)
      },
      customSearch() {
        return customSearcher(this.itemsMetadata, ID_KEY, this.filter)
      },
      customSorter() {
        return defaultTableSorter(this.headers, this.pagination.sortBy, this.pagination.descending)
      },
    },
    watch: {
      filterToggle(value) {
        if (value == filterToggles.selected) {
          this.pagination.page = 1
        }
      },
      analyteMediaTypes: {
        immediate: true,
        handler(value) {
          this.selectedMediaType = value && value[0]
        },
      },
    },
    methods: {
      ...monitoringFeatures.mapActions(['refreshCurrentMFDetails']),
      ...stations.mapActions(['getCalendarMark']),
      formatDateTime(date, format) {
        return moment(date).format(format)
      },
      clearSearch() {
        this.$nextTick(() => this.search = '')
      },
      customFilter(items, index) {
        let searchResult = this.customSearch(items, index)
        let result = []

        if (this.filterToggle == filterToggles.selected) {
          searchResult.forEach(item => {
            let findIndex = this.selected.findIndex(s => s[ID_KEY] === item[ID_KEY])
            if (findIndex > -1) {
              result.push(item)
            }
          })
        } else {
          result = searchResult
        }

        return result
      },
      toggleCurrentItems() {
        let filteredItems = this.$refs.analytesTable.filteredItems
        this.selected = mergeDistinctObjectArrays(this.selected, filteredItems, SEARCH_KEY)
      },
      toggleAll() {
        let selected = {}
        let filteredItems = this.filter.items

        this.selected.forEach(item => {
          selected[item[ID_KEY]] = item
        })


        if (filteredItems.every(item => selected[item[ID_KEY]])) {
          filteredItems.forEach(item => {
            delete selected[item[ID_KEY]]
          })
        } else {
          filteredItems.forEach(item => {
            selected[item[ID_KEY]] = item
          })
        }

        this.selected = Object.values(selected)
      },
      changeSort(column) {
        if (this.pagination.sortBy === column) {
          this.pagination.descending = !this.pagination.descending
        } else {
          this.pagination.sortBy = column
          this.pagination.descending = false
        }
      },
      onTabularClose() {
        this.viewTabularRequestData = null
      },
      getDateRange(items) {
        let startDates
        let endDates

        let dateTimeGroups = []

        if (this.selected.length) {
          startDates = this.selected.map(d => d.FirstRecordDate)
          endDates = this.selected.map(d => d.LastRecordDate)

          let temp = this.CalendarMark.DateTimeGroups.filter(x =>
            x.MediaTypeGroups.some(y => y.MediaType == this.selectedMedia &&
              y.LabAnalyteGroups.some(z => this.selected.some(i => z.AnalyteId == i.Id))
            )
          )
          dateTimeGroups = dateTimeGroups.concat(temp.map(x => x.SampleDateTime))

        } else {
          startDates = items.map(d => d.FirstRecordDate)
          endDates = items.map(d => d.LastRecordDate)

          dateTimeGroups = this.CalendarMark.DateTimeGroups.map(x => x.SampleDateTime)
        }

        this.availableData = dateTimeGroups

        this.minDate = min(...startDates)
        this.maxDate = max(...endDates)

        if (this.selectedRange && this.selectedRange.key == 'pastMonthWithData') {
          this.dates = {
            start: moment(this.maxDate).clone().add(-1, 'months'),
            end: this.maxDate,
          }
        }
        if (this.selectedRange && this.selectedRange.key == 'pastTwoWeekWithData') {
          this.dates = {
            start: moment(this.maxDate).clone().add(-14, 'days'),
            end: this.maxDate,
          }
        }
        if (this.selectedRange && this.selectedRange.key == 'pastThreeMonthsWithData') {
          this.dates = {
            start: moment(this.maxDate).clone().add(-3, 'months'),
            end: this.maxDate,
          }
        }
        if (this.selectedRange && this.selectedRange.key == 'all') {
          this.dates = {
            start: this.minDate,
            end: this.maxDate,
          }
        }
      },
      generateGraph() {
        let hasRequiredInputs = !(this.noSelectionWarning || this.invalidDatesWarning)
        if (hasRequiredInputs) {
          this.graphRequestData = {
            stationName: this.stationName,
            startDate: this.dates.start,
            endDate: this.dates.end,
            datasets: this.selected,
            guidelines: this.selectedGuidelines,
            media: this.selectedMediaType,
            includeQaQc: this.includeQaQc,
          }
          this.showErrorBox = false
        } else {
          this.showErrorBox = true
        }
      },
      onGuidelineSelectHandler(data) {
        this.selectedGuidelines = [data]
      },
      downloadData({ suppressErrors }) {
        let params = {
          suppressErrors: suppressErrors || false,
        }
        var requestData = {
          StartDate: newDateStringNoTimezone(this.dates.start),
          EndDate: newDateStringNoTimezoneAddOneDay(this.dates.end),
          IncludeDischargeData: false,
          StationId: this.discreteStation.Id,
          StationName: this.stationName,
          WaterQualityStandards: this.selectedGuidelines[0] ? this.selectedGuidelines : [],
          WaterQualityDatasetNames: this.selected.map(x => x.Analyte),
          Media: this.selectedMediaType,
          IncludeQaQc: this.includeQaQc,
          IncludeAnalyteName: this.includeAnalyteName,
        }

        let url = '/Report/DiscreteStationDetail'
        let hasRequiredInputs = !(this.noSelectionWarning || this.invalidDatesWarning)
        if (hasRequiredInputs) {
          this.loadingDownloadData = true

          let fileName = 'Station-' + this.stationName + '-Data-From-' + this.formatDateTime(this.dates.start,
              backendDateTimeFormat) + '-To-' + this
            .formatDateTime(this.dates.end, backendDateTimeFormat) + '.xlsx'
          var options = {
            params: params,
            successful: () => {
              this.loadingDownloadData = false
              this.resetErrorModal()
            },
            failedCb: (error) => {
              this.loadingDownloadData = false
              let { data, customTitle } = this.generateReportTitleAndData(error.response.data)
              this.setErrorModal(data, () => {
                this.downloadData({ suppressErrors: true })
              }, customTitle)
            },
            nodataCb: () => {
              let analyteName = requestData.WaterQualityDatasetNames.join(',')
              this.loadingDownloadData = false
              this.$store.dispatch('app/setSnackbar', {
                text: `No data available for ${analyteName} for the selected dates! Please update the query and try again.`,
                color: 'info',
                icon: 'info',
              })
            },
          }
          this.downloadByPost(url, requestData, fileName, options)
          this.showErrorBox = false
        } else {
          this.showErrorBox = true
        }
      },
      generateTabular() {
        let hasRequiredInputs = !(this.noSelectionWarning || this.invalidDatesWarning)

        if (hasRequiredInputs) {
          this.viewTabularRequestData = {
            stationName: this.stationName,
            startDate: moment(this.dates.start).format(backendDateTimeFormat),
            endDate: moment(this.dates.end).format(backendDateTimeFormat),
            datasets: this.selected,
            guidelines: this.selectedGuidelines,
            media: this.selectedMediaType,
            includeQaQc: this.includeQaQc,
            includeAnalyteName: this.includeAnalyteName,
          }

          this.showErrorBox = false
        } else {
          this.showErrorBox = true
        }
      },
      makeItemsMetadata(items) {
        let result = {}
        items.forEach(item => {
          if (this.showAnalyteName)
            result[item[ID_KEY]] = getObjectValues(item, 'DisplayAnalyte', 'Analyte')
          else
            result[item[ID_KEY]] = getObjectValues(item, SEARCH_KEY)
        })

        this.itemsMetadata = result
      },
      // Skip for now, implement later
      async fetchCalendarMarks() {
        this.allCalendarMarks = await this.$store.dispatch('stations/getCalendarMark', {
          stationName: this.stationName,
          tenant: this.currentUserGroup && this.currentUserGroup.name,
        })
      },
    },
    mounted() {
      this.fetchCalendarMarks()
    },
  }
</script>

<style>
  .clickable {
    cursor: pointer !important;
  }

  .chipFilter>.v-chip__content {
    cursor: pointer;
  }

  .switch {
    margin: 0 0 8px 0;
    padding: 0 0 0 14px;
    display: inline-block;
  }

  .switch>.v-input__control>.v-input__slot>label {
    font-size: 14px !important;
    font-weight: 500 !important;
  }
</style>